import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API_BASE, formatApiError, useAuth } from "./AuthContext";
import AssigneePicker, { useTeamMembers } from "./AssigneePicker";
import { X, Loader2, ListPlus, UserCheck, Mail, Activity } from "lucide-react";

export function TaskAssignControl({ task, onChanged, testid }) {
  const { state } = useAuth();
  const myId = state?.user?.user_id;
  const [busy, setBusy] = useState(false);
  const assign = async (uid) => {
    setBusy(true);
    try {
      await axios.patch(`${API_BASE}/tasks/${task.task_id}`, { assignee_id: uid || null });
      toast.success(uid ? (uid === myId ? "Assigned to you" : "Task reassigned") : "Task unassigned");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <AssigneePicker value={task.assignee_id} onChange={assign} compact label="Assign" disabled={busy}
                      testid={`${testid}-assignee`} />
      {task.status !== "done" && task.assignee_id !== myId && (
        <button type="button" onClick={() => assign(myId)} disabled={busy}
                className="text-[10px] uppercase tracking-wider text-[var(--accent)] hover:underline inline-flex items-center gap-0.5"
                data-testid={`${testid}-take`} title="Assign this task to yourself">
          <UserCheck size={10} /> Take it
        </button>
      )}
    </span>
  );
}

export function TaskSourceChip({ task }) {
  const src = task.source;
  if (!src) return null;
  const Icon = src.type === "activity" ? Activity : Mail;
  const body = (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded max-w-[260px] truncate" style={{ background: "var(--subtle)" }}>
      <Icon size={10} /> {src.type === "email" ? "From email" : src.type === "emails" ? "From emails" : "From activity"}{src.label ? `: ${src.label}` : ""}
    </span>
  );
  return src.type !== "activity"
    ? <Link to={`/email-triage?email_id=${src.id}`} className="hover:underline" data-testid={`task-source-${task.task_id}`}>{body}</Link>
    : <span data-testid={`task-source-${task.task_id}`}>{body}</span>;
}

export default function CreateTaskDialog({ defaults, context, onClose, onCreated }) {
  const { state } = useAuth();
  const myId = state?.user?.user_id;
  const members = useTeamMembers();
  const [f, setF] = useState({ title: defaults.title || "", description: defaults.description || "",
    due: "", priority: "medium", assignee_id: myId || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/tasks`, {
        title: f.title.trim(), description: f.description.trim() || null, priority: f.priority,
        assignee_id: f.assignee_id || null, due_at: f.due ? new Date(f.due).toISOString() : null,
        parent_kind: defaults.parent_kind || "none", parent_id: defaults.parent_id || null, source: defaults.source,
      });
      toast.success("Task created", { action: { label: "View", onClick: () => window.location.assign(`/tasks?task_id=${data.task_id}`) } });
      window.dispatchEvent(new Event("tasks:refresh-pending"));
      onCreated?.(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const input = "w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]";
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="create-task-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[var(--paper)] rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2 text-[14px] font-medium"><ListPlus size={15} /> New task</div>
          <button type="button" onClick={onClose} data-testid="create-task-close"><X size={17} /></button>
        </div>
        <div className="p-5 space-y-3">
          {context && <div className="text-[11px] text-[var(--ink-3)] truncate" data-testid="create-task-context">Linked to: {context}</div>}
          <input className={input} value={f.title} onChange={set("title")} placeholder="What needs doing?" required maxLength={240}
                 autoFocus data-testid="create-task-title" />
          <textarea className={input} rows={3} value={f.description} onChange={set("description")} maxLength={4000}
                    placeholder="Notes (optional)" data-testid="create-task-description" />
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-[var(--ink-3)]">Due
              <input type="datetime-local" className={input} value={f.due} onChange={set("due")} data-testid="create-task-due" />
            </label>
            <label className="text-[11px] text-[var(--ink-3)]">Priority
              <select className={input} value={f.priority} onChange={set("priority")} data-testid="create-task-priority">
                {["low", "medium", "high"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
          </div>
          <label className="text-[11px] text-[var(--ink-3)] block">Assignee
            <div className="flex gap-2">
              <select className={input} value={f.assignee_id} onChange={set("assignee_id")} data-testid="create-task-assignee">
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}{m.user_id === myId ? " (me)" : ""}</option>)}
              </select>
              <button type="button" className="btn-ghost whitespace-nowrap" onClick={() => setF((p) => ({ ...p, assignee_id: myId }))}
                      disabled={f.assignee_id === myId} data-testid="create-task-assign-me"><UserCheck size={13} /> Me</button>
            </div>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-[var(--border)] flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="create-task-cancel">Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy || !f.title.trim()} data-testid="create-task-submit">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ListPlus size={13} />} Create task
          </button>
        </div>
      </form>
    </div>
  );
}
